// AppShell (MGMT-PANEL-BUILD-SHELL-01, direction A "Calm operations"): the one frame every signed-in
// route sits in. Sidebar, a 56px header (breadcrumbs, light/dark toggle, phone menu button), the
// content area with the one padding scale (32px, 24px at <=1100px, 16px at <=720px; 1440px max width)
// and the one footer, drawn in flow after the content inside a full-height column so it sits at the
// bottom of a short page and after the content of a long one. At <=900px the sidebar is a drawer.
// MGMT-PANEL-SHELL-REGRESSIONS-01: on wider screens the sidebar collapses to icons (the chevron at its
// top, as before the redesign); the content area widens; the choice is kept per viewer in localStorage.
import React, { useCallback, useEffect, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, Outlet, matchRoutes, useLocation } from 'react-router-dom';
import Sidebar from '../navigation/Sidebar';
import { usePanelTheme } from '../../theme/PanelThemeProvider';
import { FONT_SANS } from '../../theme/united';
import { SIGNED_IN_ROUTES } from '../../routes';

export const SIDEBAR_PREF_KEY = 'united.panel.sidebar';
export const PHONE_QUERY = '(max-width:900px)';

let storageWarned = false;
function warnStorageOnce(op, e) {
    // Rule 3a: storage that throws is logged once; the choice then lasts for this page only.
    if (storageWarned) return;
    storageWarned = true;
    console.warn(`[SIDEBAR_PREF_STORAGE] ${JSON.stringify({ op, error: String((e && e.message) || e) })}`);
}
export function readSidebarCollapsed() {
    try {
        return window.localStorage.getItem(SIDEBAR_PREF_KEY) === 'collapsed';
    } catch (e) {
        warnStorageOnce('read', e);
        return false;
    }
}
export function writeSidebarCollapsed(collapsed) {
    try {
        window.localStorage.setItem(SIDEBAR_PREF_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch (e) {
        warnStorageOnce('write', e);
    }
}

export const FOOTER_TEXT = (year = new Date().getFullYear()) => `© ${year} United · Admin`;

const noTitleLogged = new Set();
export function _resetNoTitleLog() { noTitleLogged.clear(); }

export function crumbsFor(pathname, routes = SIGNED_IN_ROUTES) {
    const matches = matchRoutes(routes.map((r) => ({ path: r.path, meta: r })), pathname);
    const meta = matches && matches.length ? matches[matches.length - 1].route.meta : null;
    if (!meta || !meta.title) {
        // Rule 3a: a route the shell cannot name is logged once per path.
        if (!noTitleLogged.has(pathname)) {
            noTitleLogged.add(pathname);
            console.warn(`[SHELL_NO_TITLE] ${JSON.stringify({ path: pathname })}`);
        }
        return null;
    }
    return { group: meta.group, parent: meta.parent || null, title: meta.title };
}

const css = `
.u-app{display:flex;width:100%;height:100vh;background:var(--bg);color:var(--ink);font-family:${FONT_SANS}}
.u-main{flex:1;min-width:0;height:100vh;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;position:relative;--pad:32px}
.u-top{position:sticky;top:0;z-index:40;height:56px;flex:none;display:flex;align-items:center;gap:12px;padding:0 var(--pad);background:var(--top);-webkit-backdrop-filter:saturate(1.2) blur(14px);backdrop-filter:saturate(1.2) blur(14px);border-bottom:1px solid var(--line)}
.u-crumbs{display:flex;align-items:center;gap:8px;min-width:0;font-size:14px;color:var(--muted);white-space:nowrap;overflow:hidden}
.u-crumbs a{color:inherit;text-decoration:none}.u-crumbs a:hover{color:var(--ink)}
.u-crumbs .sep{opacity:.5}.u-crumbs b{color:var(--ink);font-weight:500;overflow:hidden;text-overflow:ellipsis}
.u-top .sp{flex:1}
.u-iconbtn{width:40px;height:40px;border-radius:10px;border:1px solid transparent;background:transparent;display:inline-grid;place-items:center;cursor:pointer;color:var(--muted);flex:none;transition:background .2s,color .2s}
.u-iconbtn:hover{background:var(--hover);color:var(--ink)}
.u-iconbtn:focus-visible{outline:2px solid var(--accent)!important;outline-offset:2px}
.u-menu{display:none}
.u-content{flex:1 0 auto;width:100%;max-width:1440px;margin:0 auto;padding:var(--pad);min-width:0}
.u-foot{flex:none;border-top:1px solid var(--line)}
.u-foot div{max-width:1440px;margin:0 auto;padding:20px var(--pad);color:var(--muted);font-size:13px}
.u-scrim{display:none}
@media (max-width:1100px){.u-main{--pad:24px}}
@media (max-width:900px){
 .u-side{position:fixed;left:0;top:0;bottom:0;z-index:1250;transform:translateX(-100%);transition:transform .2s ease;height:100%;visibility:hidden}
 .u-app.nav-open .u-side{transform:none;visibility:visible}
 .u-app.nav-open .u-scrim{display:block;position:fixed;inset:0;background:var(--scrim);z-index:1240}
 .u-menu{display:inline-grid}
}
@media (max-width:720px){.u-main{--pad:16px}.u-top{gap:6px}.u-crumbs .hide-sm{display:none}}
@media (prefers-reduced-motion:reduce){.u-side{transition:none}}
`;

const SunIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
);
const MoonIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" /></svg>
);
const MenuIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
);

export default function AppShell({ handleLogout, children }) {
    const location = useLocation();
    const { scheme, toggleScheme } = usePanelTheme();
    const [navOpen, setNavOpen] = useState(false);
    const closeNav = useCallback(() => setNavOpen(false), []);
    const [collapsedPref, setCollapsedPref] = useState(readSidebarCollapsed);
    const isPhone = useMediaQuery(PHONE_QUERY, { noSsr: true });
    // Phones keep PB1's drawer, always full width with labels; the collapsed choice applies above 900px.
    const collapsed = collapsedPref && !isPhone;
    const toggleCollapsed = useCallback(() => {
        setCollapsedPref((c) => {
            writeSidebarCollapsed(!c);
            return !c;
        });
    }, []);

    useEffect(() => { setNavOpen(false); }, [location.pathname]);
    useEffect(() => {
        if (!navOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navOpen]);

    const crumbs = crumbsFor(location.pathname);
    const nextScheme = scheme === 'dark' ? 'light' : 'dark';

    return (
        <div className={`u-app${navOpen ? ' nav-open' : ''}${collapsed ? ' side-collapsed' : ''}`} data-shell>
            <style>{css}</style>
            <Sidebar
                handleLogout={handleLogout}
                onNavigate={closeNav}
                collapsed={collapsed}
                onToggleCollapse={isPhone ? undefined : toggleCollapsed}
            />
            <div className="u-scrim" onClick={closeNav} data-shell-scrim aria-hidden="true" />
            <div className="u-main" data-shell-scroll>
                <header className="u-top" data-shell-header>
                    <button
                        type="button"
                        className="u-iconbtn u-menu"
                        aria-label="Open menu"
                        aria-controls="panel-sidebar"
                        aria-expanded={navOpen}
                        onClick={() => setNavOpen(true)}
                        data-shell-menu
                    >
                        <MenuIcon />
                    </button>
                    <nav className="u-crumbs" aria-label="Breadcrumb" data-shell-crumbs>
                        {crumbs && (
                            <>
                                <span className="hide-sm">{crumbs.group}</span>
                                <span className="sep hide-sm">/</span>
                                {crumbs.parent && (
                                    <>
                                        <Link to={crumbs.parent.to}>{crumbs.parent.label}</Link>
                                        <span className="sep">/</span>
                                    </>
                                )}
                                <b>{crumbs.title}</b>
                            </>
                        )}
                    </nav>
                    <span className="sp" />
                    <button
                        type="button"
                        className="u-iconbtn"
                        onClick={toggleScheme}
                        aria-label={`Switch to ${nextScheme} mode`}
                        title={`Switch to ${nextScheme} mode`}
                        data-shell-theme-toggle
                    >
                        {scheme === 'dark' ? <SunIcon /> : <MoonIcon />}
                    </button>
                </header>
                <main className="u-content" data-shell-content>
                    {children !== undefined ? children : <Outlet />}
                </main>
                <footer className="u-foot" data-shell-footer>
                    <div>{FOOTER_TEXT()}</div>
                </footer>
            </div>
        </div>
    );
}
